import { useMemo, useState, useEffect as _useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Trash2, Plus, Loader2 } from "lucide-react";
import { brl, api } from "@/lib/api";

export const METHODS = [
  { value: "dinheiro", label: "Dinheiro" },
  { value: "pix", label: "Pix" },
  { value: "debito", label: "Cartão de débito" },
  { value: "credito", label: "Cartão de crédito" },
  { value: "boleto", label: "Boleto" },
  { value: "financiamento", label: "Financiamento" },
];

export const METHOD_LABELS = Object.fromEntries(METHODS.map((m) => [m.value, m.label]));

const emptyLine = () => ({ method: "pix", amount: "", machine_id: "" });

const MACHINE_METHODS = new Set(["debito", "pix", "credito"]);

export function PaymentDialog({ open, onOpenChange, total, initial, onConfirm, confirmLabel = "Salvar", skippable = true, onSkip }) {
  const [lines, setLines] = useState(
    initial?.methods?.length
      ? initial.methods.map((m) => ({ method: m.method, amount: String(m.amount), machine_id: m.machine_id || "" }))
      : [emptyLine()]
  );
  const [installments, setInstallments] = useState(initial?.installments ?? "");
  const [financier, setFinancier] = useState(initial?.financier || "");
  const [note, setNote] = useState(initial?.internal_note || "");
  const [saving, setSaving] = useState(false);

  const sum = useMemo(() => lines.reduce((a, l) => a + (parseFloat(l.amount) || 0), 0), [lines]);
  const hasCredit = lines.some((l) => l.method === "credito");
  const hasFin = lines.some((l) => l.method === "financiamento");
  const diff = total != null ? +(Number(total) - sum).toFixed(2) : 0;

  const [fees, setFees] = useState(null);
  const [machines, setMachines] = useState([]);
  _useEffect(() => {
    api.get("/shop-settings/payment-fees").then(({ data }) => setFees(data)).catch(() => setFees({ debito: 0, pix: 0, credito: {} }));
    api.get("/shop-settings/payment-machines").then(({ data }) => {
      const act = (data || []).filter((m) => m.active);
      setMachines(act);
      setLines((p) => p.map((l) => (MACHINE_METHODS.has(l.method) && !l.machine_id && act.length)
        ? { ...l, machine_id: (act.find((m) => m.is_default) || act[0]).id } : l));
    }).catch(() => setMachines([]));
  }, []);
  const machineById = useMemo(() => Object.fromEntries(machines.map((m) => [m.id, m])), [machines]);
  const defaultMachineId = useMemo(() => (machines.find((m) => m.is_default) || machines[0])?.id || "", [machines]);
  const feeFor = (line, amt) => {
    const m = line.machine_id ? machineById[line.machine_id] : null;
    const n = parseInt(installments) || 1;
    const legacy = line.method === "debito" ? Number(fees?.debito || 0) : line.method === "pix" ? Number(fees?.pix || 0) : line.method === "credito" ? Number(fees?.credito?.[String(n)] || 0) : 0;
    let rate = m
      ? (line.method === "debito" ? Number(m.debito || 0) : line.method === "pix" ? Number(m.pix || 0) : line.method === "credito" ? Number(m.credito?.[String(n)] || 0) : 0)
      : legacy;
    if (m && line.method === "credito" && m.advance_enabled) rate += Number(m.advance_rate || 0);
    return { rate, fee: +((amt * rate) / 100).toFixed(2) };
  };
  const totalFee = lines.reduce((a, l) => a + feeFor(l, parseFloat(l.amount) || 0).fee, 0);
  const liquid = +(sum - totalFee).toFixed(2);

  const addLine = () => setLines((p) => [...p, emptyLine()]);
  const rmLine = (i) => setLines((p) => p.filter((_, idx) => idx !== i));
  const setLine = (i, k, v) => setLines((p) => p.map((l, idx) => {
    if (idx !== i) return l;
    const next = { ...l, [k]: v };
    if (k === "method") next.machine_id = MACHINE_METHODS.has(v) ? (l.machine_id || defaultMachineId) : "";
    return next;
  }));

  const submit = async () => {
    const payload = {
      methods: lines
        .filter((l) => parseFloat(l.amount) > 0)
        .map((l) => ({ method: l.method, amount: parseFloat(l.amount), machine_id: MACHINE_METHODS.has(l.method) ? (l.machine_id || null) : null })),
      installments: hasCredit || hasFin ? parseInt(installments) || null : null,
      financier: hasFin ? financier.trim() || null : null,
      internal_note: note.trim() || null,
    };
    if (!payload.methods.length) return;
    setSaving(true);
    try {
      await onConfirm(payload);
    } finally {
      setSaving(false);
    }
  };

  const fillRemaining = () => {
    if (total == null) return;
    const otherSum = lines.slice(0, -1).reduce((a, l) => a + (parseFloat(l.amount) || 0), 0);
    setLine(lines.length - 1, "amount", String(+(Number(total) - otherSum).toFixed(2)));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase">Forma de pagamento</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Interno — não aparece para o cliente, nem no PDF, nem no e-mail. Entra no faturamento.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {total != null && (
            <div className="text-sm text-zinc-300 flex items-center justify-between rounded-lg bg-zinc-900 border border-zinc-800 px-3 py-2">
              <span>Total da OS</span>
              <span className="font-bold text-green-neon" data-testid="payment-total">{brl(total)}</span>
            </div>
          )}
          {lines.map((l, i) => (
            <div key={i} data-testid={`payment-line-${i}`} className="space-y-2">
              <div className="grid grid-cols-[1fr_120px_40px] gap-2 items-end">
              <div>
                <Label className="text-xs font-mono-caps text-zinc-400">Forma</Label>
                <Select value={l.method} onValueChange={(v) => setLine(i, "method", v)}>
                  <SelectTrigger data-testid={`payment-method-${i}`} className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    {METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-mono-caps text-zinc-400">Valor</Label>
                <Input data-testid={`payment-amount-${i}`} inputMode="decimal" value={l.amount}
                  onChange={(e) => setLine(i, "amount", e.target.value.replace(",", "."))}
                  placeholder="0,00" className="mt-1 bg-zinc-900 border-zinc-800" />
              </div>
              <button type="button" aria-label="Remover" onClick={() => rmLine(i)} disabled={lines.length === 1}
                className="h-10 w-10 rounded-md border border-zinc-800 bg-zinc-900 text-zinc-400 hover:text-red-300 disabled:opacity-30">
                <Trash2 className="h-4 w-4 mx-auto" />
              </button>
              </div>
              {MACHINE_METHODS.has(l.method) && machines.length > 0 && (
                <div>
                  <Label className="text-xs font-mono-caps text-zinc-400">Maquininha</Label>
                  <Select value={l.machine_id || ""} onValueChange={(v) => setLine(i, "machine_id", v)}>
                    <SelectTrigger data-testid={`payment-machine-${i}`} className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800">
                      {machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}{m.is_default ? " (padrão)" : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          ))}
          <div className="flex items-center justify-between">
            <Button type="button" data-testid="payment-add" variant="outline" size="sm" onClick={addLine} className="bg-zinc-900 border-zinc-800">
              <Plus className="h-4 w-4 mr-1" /> Adicionar forma
            </Button>
            {total != null && (
              <button type="button" onClick={fillRemaining} className="text-xs text-amber-brand hover:underline">
                Preencher restante ({brl(diff)})
              </button>
            )}
          </div>
          {(hasCredit || hasFin) && (
            <div>
              <Label className="text-xs font-mono-caps text-zinc-400">Parcelas</Label>
              <Input data-testid="payment-installments" type="number" min={1} max={48} value={installments}
                onChange={(e) => setInstallments(e.target.value)}
                className="mt-1 bg-zinc-900 border-zinc-800" placeholder="1" />
            </div>
          )}
          {hasFin && (
            <div>
              <Label className="text-xs font-mono-caps text-zinc-400">Financeira</Label>
              <Input data-testid="payment-financier" value={financier} onChange={(e) => setFinancier(e.target.value)}
                className="mt-1 bg-zinc-900 border-zinc-800" placeholder="Ex.: Banco XYZ" />
            </div>
          )}
          <div>
            <Label className="text-xs font-mono-caps text-zinc-400">Observação interna</Label>
            <Input data-testid="payment-note" value={note} onChange={(e) => setNote(e.target.value)}
              className="mt-1 bg-zinc-900 border-zinc-800" placeholder="Ex.: cliente deixou chave sobressalente" />
          </div>
          {total != null && Math.abs(diff) > 0.009 && (
            <div data-testid="payment-diff-warning" className={`text-xs ${diff > 0 ? "text-amber-300" : "text-red-300"}`}>
              Diferença: {brl(Math.abs(diff))} {diff > 0 ? "faltam" : "excedem o total"}
            </div>
          )}
          {totalFee > 0 && (
            <div data-testid="payment-fees" className="rounded-lg border border-sky-800/50 bg-sky-950/30 p-2 text-xs space-y-0.5">
              <div className="flex justify-between text-sky-200"><span>Taxa da maquininha</span><span data-testid="payment-fee-total" className="tabular-nums">− {brl(totalFee)}</span></div>
              <div className="flex justify-between text-sky-100 font-bold"><span>Valor líquido</span><span data-testid="payment-liquid" className="tabular-nums">{brl(liquid)}</span></div>
            </div>
          )}
        </div>
        <DialogFooter>
          {skippable && (
            <Button type="button" variant="ghost" onClick={onSkip} className="text-zinc-400 hover:text-zinc-200" data-testid="payment-skip">
              Pular por enquanto
            </Button>
          )}
          <Button type="button" data-testid="payment-save" onClick={submit} disabled={saving || !lines.some((l) => parseFloat(l.amount) > 0)}
            className="bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
